<template>
	<v-card class="forum-card elevation-0 d-flex flex-column">
		<div class="forum-card__container">
			<div class="forum-card__outer">
				<div class="d-flex forum-card__list px-0">
					<div
						v-if="forum && forum.user"
						class="d-flex justify-space-between align-center pa-3"
						style="width: 100%"
					>
						<div
							:style="{
								cursor: canOpenProfile(forum.user.user_id) ? 'pointer' : 'default',
							}"
							@click="toggleToUser(forum.user.user_id)"
							class="d-flex align-center u-cursor-pointer"
						>
							<Avatar
								class="mr-2 category-card__avatar"
								:size="50"
								:avatar="forum.user.user_logo"
							/>
							<div class="forum-card__meta">
								<span class="forum-card__meta-top text-2-lines">
									{{ forum.user.visible_username }}
								</span>
								<span class="forum-card__meta-bottom text-2-lines">
									{{ forum.user.second_profile_row }}
								</span>
							</div>
						</div>
						<div class="forum-card__meta-time">
							<Icon
								v-if="forum.pin_to_top"
								type="pinned"
								:size="13"
								color="var(--primary_color)"
							/>
							<div class="d-flex flex-column align-end" style="height: 100%">
								<div style="margin-top: auto">
									{{ createdTime }}
								</div>
							</div>
						</div>
					</div>
					<div class="forum-card__info">
						<PhotoSlider
							v-if="photos.length > 0"
							:photos="photos"
							@openModal="openModal"
						/>
						<div
							class="forum-card__info__forum-title px-3"
							v-if="forum && forum.thread_title"
						>
							<span>{{ forum.thread_title }}</span>
						</div>
						<div
							v-if="this.forum && forum.thread_text"
							class="forum-card__info__forum-text px-3"
							v-html="this.postDescription(forum.thread_text)"
						></div>
						<div
							v-if="forum.uploads && forum.uploads.length"
							class="forum-card__attachment"
						>
							<div class="forum-card__attachment__title">Attachment</div>
							<div class="forum-card__attachment__files">
								<div
									@click.prevent="toggleToFiles(false)"
									v-for="attachment in this.forum.uploads.slice(0, 7)"
									:key="attachment.upload_id"
								>
									<PostAttachments
										backgroundColor="#F3F3F6"
										:attachment="attachment"
									/>
								</div>
								<div
									v-if="forum.uploads.length > 7"
									@click.prevent="toggleToFiles(true, countAttachments)"
									class="more-attachments"
								>
									+{{ countAttachments }}
								</div>
							</div>
						</div>
						<v-divider />
					</div>
					<v-dialog
						v-if="mobileLink"
						v-model="mobileLink"
						max-width="588px"
						width="100%"
						persistent
					>
						<v-card class="exhibitor-page__modal">
							<div class="exhibitor-page__modal__title">
								This screen is not available in the Web App, but you can access it
								through the mobile app
							</div>
							<button
								type="button"
								@click="toggleCloseMobileLink"
								class="exhibitor-page__modal__btn"
							>
								OK
							</button>
						</v-card>
					</v-dialog>
				</div>

				<div class="forum-card__custom_container">
					<div class="forum-card__custom_container__icons">
						<div
							@click="onLike"
							class="forum-card__custom_container__icons__info u-cursor-pointer"
						>
							<Icon
								:isActive="!!isLiked"
								type="hearth"
								:color="isLiked ? 'var(--primary_color)' : '#9b9b9b'"
								:size="24"
							/>
							<div
								:style="{ color: isLiked ? 'var(--primary_color)' : '#9b9b9b' }"
								class="forum-card__counter-num"
							>
								{{ likes }}
							</div>
						</div>
						<div class="forum-card__custom_container__icons__info">
							<Icon type="comment" color="#9b9b9b" :size="24" />
							<div class="forum-card__counter-num">
								{{ forum.comments_count }}
							</div>
						</div>
						<div
							v-if="forum.uploads.length"
							@click="toggleToFiles(false)"
							class="forum-card__custom_container__icons__info u-cursor-pointer"
						>
							<Icon type="clip" color="#9b9b9b" :size="24" />
							<div class="forum-card__counter-num">
								{{ forum.uploads.length }}
							</div>
						</div>
					</div>
					<div
						class="forum-card__follow"
						:style="{ color: bookmarked ? '#9b9b9b' : 'var(--primary_color)' }"
						@click="addToBookmark"
					>
						<Icon
							:type="bookmarked ? 'check' : 'plus'"
							:size="24"
							:color="bookmarked ? '#9b9b9b' : 'var(--primary_color)'"
						/>
						{{ bookmarked ? 'Following' : 'Follow' }}
					</div>
				</div>

				<div class="post-card__comments-view">
					<Comment
						v-for="item in normalizedComments"
						:key="item.id"
						:comment="item"
						:post="{
							thread_id: item.threadId,
							user: item.user,
						}"
						:isAuthorDiscussionId="isAuthorDiscussionId"
						:forum="forum"
					/>
				</div>
				<v-divider v-if="this.comments.length === 0" />
				<div v-if="!isCommentActive" class="d-flex align-center">
					<div style="width: 100%" v-if="this.isAuthenticated">
						<CommentForm
							:avatar="this.profile.user_logo"
							:parent="this.forum"
							:isForum="true"
						/>
					</div>
				</div>
			</div>
		</div>
		<PhotoModal
			v-if="isModalOpen"
			:post="forum"
			@close="isModalOpen = false"
			:initial-index="currentPhotoIndex"
		/>
	</v-card>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import Avatar from '@/components/Avatar'
import CommentForm from '@/components/Forms/CommentForm.vue'
import { mapActions, mapGetters } from 'vuex'
import { normalizeComment, calcDateAgo } from '@/utils/helpers'
import Comment from '@/components/Comment.vue'
import PostAttachments from '@/components/Post/PostAttachments.vue'
import linkifyThreadText from '@/mixins/linkifyThreadText'
import PhotoModal from '@/components/PhotoModal.vue'
import PhotoSlider from '@/components/PhotoSlider.vue'
import { toggleDislike, toggleLike } from '@/api/post'
import Dwh from '@/mixins/Dwh'

Vue.use(VueScrollTo)

export default {
	name: 'ForumCard',
	components: {
		PhotoSlider,
		PhotoModal,
		PostAttachments,
		CommentForm,
		Comment,
		Avatar,
	},
	mixins: [linkifyThreadText, Dwh],
	data: () => ({
		mobileLink: false,
		isModalOpen: false,
		isLiked: false,
		likes: 0,
		currentPhotoIndex: 0,
		bookmarked: null,
	}),
	props: {
		forum: {
			type: Object,
			default: () => ({}),
		},
	},
	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated', 'mapRegisterToken']),
		...mapGetters('forum', ['comments', 'isCommentActive']),
		...mapGetters('settings', ['appCore']),
		...mapGetters('app', ['hasOtherProfileAccess']),
		isAuthorDiscussionId() {
			return this.forum.user.user_id
		},
		createdTime() {
			return calcDateAgo(this.forum.publish_timestamp)
		},
		photos() {
			if (this.forum.slider_images?.length > 0) {
				return this.forum.slider_images.map((image) => image.image_url)
			}
			return []
		},
		countAttachments() {
			if (Array.isArray(this.forum.uploads)) {
				return this.forum.uploads.length - 7
			}
			return 0
		},

		/**
		 * @returns Array
		 */
		normalizedComments: function () {
			return this.comments.map((item) => normalizeComment(item))
		},
	},
	methods: {
		...mapActions('auth', ['getMapRegisterToken']),
		...mapActions('event', ['toggleBookmark']),
		dwhAllAttachmentsHandler(count) {
			this.sendTrigger({
				category: 'Discussion Post',
				action: 'click_view_attachments_discussion',
				module: 'Forums / Communities',
				title: this.forum.thread_title,
				id1: this.forum.thread_id,
				data: count,
			})
		},
		dwhBookmarkHandler(type) {
			let action = type ? 'click_follow_discussion' : 'click_unfollow_discussion'
			this.sendTrigger({
				category: 'Discussion Post',
				action: action,
				module: 'Forums / Communities',
				title: this.forum.thread_title,
				id1: this.forum.thread_id,
			})
		},
		onLike() {
			if (this.isLiked) {
				if (this.likes === 0) {
					return
				}
				this.likes--
				toggleDislike(this.forum.thread_id)
			} else if (!this.isLiked) {
				this.likes++
				toggleLike({ thread_id: this.forum.thread_id })
			}

			this.isLiked = !this.isLiked
		},
		toggleToFiles(isButton, count) {
			this.$router.push({
				name: 'forum-file-by-id',
				params: {
					id: this.forum.thread_id,
				},
			})
			if (isButton) {
				this.dwhAllAttachmentsHandler(count)
			}
		},
		openModal(index) {
			this.currentPhotoIndex = index
			this.sendTrigger({
				category: 'Discussion Post',
				action: 'click_view_photo_discussion',
				module: 'Forums / Communities',
				title: this.forum.thread_title,
				id1: this.forum.thread_id,
				data: String(index),
			})
			this.isModalOpen = true
		},
		toggleCloseMobileLink() {
			this.mobileLink = false
		},
		canOpenProfile(id) {
			const isMe = this.isCurrentUser(id)
			return isMe || this.hasOtherProfileAccess
		},
		toggleToUser(id) {
			const isMe = this.isCurrentUser(id)
			if ((!isMe && !this.hasOtherProfileAccess) || !this.forum || !this.forum.user) {
				return
			}
			if (isMe) {
				this.$router.push({ name: 'my-account' })
			} else {
				this.$router.push({ name: 'user-by-id', params: { id } })
			}
		},
		async addToBookmark() {
			const newStatus = this.bookmarked ? 0 : 1
			this.dwhBookmarkHandler(newStatus)
			await this.toggleBookmark({
				user_id: this.profile.user_id,
				bookmark_entity_type: this.forum.bookmark.bookmark_entity_type,
				bookmark_status: newStatus,
				bookmark_entity_id: this.forum.bookmark.bookmark_entity_id,
			})
			this.bookmarked = newStatus
		},
		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const { user_id: currentUserId } = this.profile
			return userId === currentUserId
		},
	},
	created() {
		if (this.forum) {
			this.isLiked = this.forum.upvote
			this.likes = this.forum.upvote_count
			this.bookmarked = this.forum.bookmark.is_bookmarked
		}
	},
}
</script>

<style lang="scss">
.forum-card {
	background: #f1f1f1 !important;

	&__container {
		width: 100%;
		height: 100%;
		border-radius: 8px !important;
		background: #ffffff !important;
		border: 1px solid #e2e2e2 !important;
		border-bottom: none !important;
	}
	&__custom_container {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 12px;
		&__icons {
			display: flex;
			align-items: center;
			gap: 16px;
			&__info {
				display: flex;
				align-items: center;
				gap: 4px;
			}
		}
	}
	&__info {
		display: flex;
		flex-direction: column;
		gap: 12px;
		font-family: 'Inter', sans-serif;
		&__forum-title {
			font-weight: 700;
			font-size: 20px;
			line-height: 22px;
			letter-spacing: 0;
			color: var(--primary_color);
		}
		&__forum-text {
			font-weight: 400;
			font-size: 16px;
			line-height: 140%;
			letter-spacing: 0;
			color: #4a4a4a;
		}
	}
	&__attachment {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding: 0 12px;
		&__title {
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a;
		}
		&__files {
			display: flex;
			align-items: center;
		}
	}
	&__counter {
		&-num {
			color: #9b9b9b;
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
		}
	}

	&__follow {
		display: flex;
		align-items: center;
		gap: 8px;
		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0;
		text-align: center;
		text-decoration: unset;
		cursor: pointer;
	}

	&__meta {
		min-height: 46px;
		display: flex;
		word-break: break-word;
		justify-content: space-between;
		flex-direction: column;

		&-bottom {
			color: #4a4a4a;
			font-family: Inter, sans-serif;
			font-weight: 400;
			font-size: 15px;
			line-height: 20px;
			letter-spacing: 0;
		}

		&-top {
			color: var(--primary_color);
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
		}

		&-time {
			height: 46px;
			display: flex;
			flex-direction: column;
			align-items: flex-end;
			color: #9b9b9b;
			font-family: Inter, sans-serif;
			font-weight: 400;
			font-size: 13px;
			line-height: 20px;
			letter-spacing: 0;
			white-space: nowrap;
			&__button--active {
				width: 24px;
				height: 24px;
				border: 1.5px solid #0055a3;
				box-shadow: 0 0 0 2px #0055a333;
				border-radius: 4px;
				display: flex;
				align-items: center;
				justify-content: center;
			}
			&__button {
				position: relative;
				width: 24px;
				height: 24px;
				&__modal {
					font-weight: 400;
					font-size: 14px;
					line-height: 100%;
					letter-spacing: 0;
					position: absolute;
					top: 42px;
					right: 12px;
					width: 250px;
					height: 72px;
					border-radius: 8px;
					background: #ffffff;
					color: var(--accent-color);
					border: 1px solid #e2e2e2;
					font-family: 'Inter', sans-serif;
					display: flex;
					gap: 8px;
					align-items: center;
					padding: 24px;
				}
			}
		}
	}

	&__outer {
	}

	&__list {
		position: relative;
		flex-direction: column;
	}

	&__title {
		color: #4a4a4a !important;
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: 400;
		letter-spacing: 0;
		line-height: 17.07px;
		font-style: normal;
		word-break: break-word;
		white-space: pre-wrap;
	}

	&__block-view {
		flex-direction: column;
		height: 73%;

		.post-preview-card__thumbnail-outer img {
			max-width: 100%;
		}
	}

	&__content {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: 500;
		font-size: 16px;
		line-height: 21px;
	}

	&__thumbnail {
		height: auto;
		width: 100%;
	}

	&__menu-dots {
		margin-left: 10px !important;
	}

	@media screen and (max-width: 1904px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 75%;
		}
	}
	@media screen and (max-width: 1264px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 66%;
		}
	}

	@media screen and (min-width: 1264px) {
		&__container {
			max-width: 600px;
		}
	}
	@media screen and (max-width: 768px) {
		&__thumbnail {
			height: 100%;
			max-width: 100%;
		}
		&__default-thumbnail {
			max-height: 250px;
			height: 100%;
		}
		&__outer {
			flex-direction: column;
		}
	}
}
</style>
